import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { uploadedInvoiceService } from '../services/uploadedInvoiceService'
import type { UploadedInvoice } from '../types/invoice'
import { 
  CameraIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  EyeIcon,
  TrashIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import ProfileDropdown from '../components/ProfileDropdown'

const UploadedInvoiceViewPage = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showSuccess, showError, showConfirm } = useNotification()
  const [invoice, setInvoice] = useState<UploadedInvoice | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    if (user && id) {
      loadInvoice()
    }
  }, [user, id])

  const loadInvoice = async () => {
    if (!id || !user) return
    
    setIsLoading(true)
    try {
      const result = await uploadedInvoiceService.getUploadedInvoices(user.id)
      
      if (result.success && result.invoices) {
        const foundInvoice = result.invoices.find(inv => inv.id === id)
        if (foundInvoice) {
          setInvoice(foundInvoice)
        } else {
          console.error('Invoice not found')
          navigate('/invoices')
        }
      } else {
        console.error('Failed to load uploaded invoices:', result.error)
        navigate('/invoices')
      }
    } catch (error) {
      console.error('Error loading invoice:', error)
      navigate('/invoices')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMarkAsPaid = async () => {
    if (!invoice) return
    
    setIsUpdating(true)
    try {
      const result = await uploadedInvoiceService.markAsPaid(
        invoice.id, 
        invoice.amount, 
        'Manual', 
        'Marked as paid from uploaded invoice view'
      )
      
      if (result.success) {
        showSuccess(
          'Payment Recorded',
          'Invoice has been marked as paid successfully.'
        )
        loadInvoice() // Refresh the invoice data
      } else {
        showError('Payment Failed', result.error || 'Failed to mark invoice as paid.')
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error)
      showError('Payment Failed', 'An unexpected error occurred.')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteInvoice = () => {
    if (!invoice) return
    
    showConfirm(
      'Delete Uploaded Invoice',
      `Are you sure you want to delete "${invoice.originalFileName}"? This action cannot be undone.`,
      async () => {
        try {
          const result = await uploadedInvoiceService.deleteUploadedInvoice(invoice.id)
          
          if (result.success) {
            showSuccess('Invoice Deleted', 'The uploaded invoice has been deleted successfully.')
            navigate('/invoices')
          } else {
            showError('Delete Failed', result.error || 'Failed to delete invoice.')
          }
        } catch (error) {
          console.error('Error deleting invoice:', error)
          showError('Delete Failed', 'An unexpected error occurred.')
        }
      }
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileType: string) => {
    if (fileType === 'application/pdf') {
      return <DocumentTextIcon className="w-6 h-6 text-red-400" />
    }
    return <DocumentTextIcon className="w-6 h-6 text-blue-400" />
  }

  const getStatusIcon = (status: UploadedInvoice['status']) => {
    switch (status) {
      case 'draft':
        return <PencilIcon className="w-4 h-4 text-gray-400" />
      case 'sent':
        return <ArrowUpIcon className="w-4 h-4 text-blue-400" />
      case 'viewed':
        return <EyeIcon className="w-4 h-4 text-purple-400" />
      case 'paid':
        return <CheckCircleIcon className="w-4 h-4 text-green-400" />
      case 'overdue':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-400" />
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4 text-gray-400" />
      default:
        return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: UploadedInvoice['status']) => {
    switch (status) {
      case 'draft':
        return 'text-gray-400 bg-gray-500/20'
      case 'sent':
        return 'text-blue-400 bg-blue-500/20'
      case 'viewed':
        return 'text-purple-400 bg-purple-500/20'
      case 'paid':
        return 'text-green-400 bg-green-500/20'
      case 'overdue':
        return 'text-red-400 bg-red-500/20'
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/20'
      default:
        return 'text-gray-400 bg-gray-500/20'
    }
  }

  if (!user) {
    return <div>Please log in to view invoices.</div>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading invoice...</p>
        </div>
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8 text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Invoice Not Found</h2>
          <p className="text-gray-400 mb-6">The invoice you're looking for doesn't exist or you don't have permission to view it.</p>
          <Link to="/invoices">
            <button className="btn-primary">Back to Invoices</button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <motion.button 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-6"
            >
              <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors font-medium">
                Dashboard
              </Link>
              <ProfileDropdown />
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Link 
            to="/invoices"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Invoices</span>
          </Link>
        </motion.div>

        {/* Invoice Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-mockup rounded-xl p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <h1 className="text-3xl font-bold text-white">
                  {invoice.title || invoice.originalFileName}
                </h1>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
                  {getStatusIcon(invoice.status)}
                  <span className="ml-1 capitalize">{invoice.status}</span>
                </div>
                {invoice.paymentStatus === 'paid' && (
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-green-400 bg-green-500/20">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Paid
                  </div>
                )}
              </div>
              <h2 className="text-xl text-gray-300 mb-1">Uploaded Invoice</h2>
              <p className="text-gray-400">
                {invoice.issueDate && `Created ${formatDate(invoice.issueDate)}`}
                {invoice.dueDate && ` • Due ${formatDate(invoice.dueDate)}`}
                {!invoice.issueDate && !invoice.dueDate && `Uploaded ${formatDate(invoice.uploadedAt)}`}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {invoice.status !== 'paid' && invoice.amount && (
                <button
                  onClick={handleMarkAsPaid}
                  disabled={isUpdating}
                  className="btn-success flex items-center justify-center space-x-2 disabled:opacity-50 w-full sm:w-auto"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>Mark as Paid</span>
                </button>
              )}
              
              <Link to={`/uploaded-invoices/${invoice.id}/edit`} className="w-full sm:w-auto">
                <button className="btn-secondary flex items-center justify-center space-x-2 w-full">
                  <PencilIcon className="w-5 h-5" />
                  <span>Edit</span>
                </button>
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Invoice Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Client Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <UserIcon className="w-6 h-6" />
                <span>Bill To</span>
              </h3>
              
              {invoice.clientName ? (
                <div className="space-y-3">
                  <div>
                    <h4 className="text-lg font-medium text-white">{invoice.clientName}</h4>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">Client information not available</p>
              )}
            </motion.div>

            {/* File Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <DocumentTextIcon className="w-6 h-6" />
                <span>File Details</span>
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl">
                  {getFileIcon(invoice.fileType)}
                  <div className="flex-1">
                    <h4 className="font-medium text-white">{invoice.originalFileName}</h4>
                    <p className="text-sm text-gray-400">
                      {formatFileSize(invoice.fileSize)} • {invoice.fileType}
                    </p>
                  </div>
                  <button
                    onClick={() => window.open(invoice.fileUrl, '_blank')}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <EyeIcon className="w-4 h-4" />
                    <span>View File</span>
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Additional Information */}
            {invoice.notes && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-mockup rounded-xl p-6"
              >
                <h3 className="text-xl font-semibold text-white mb-4">Additional Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Notes</h4>
                    <p className="text-white whitespace-pre-wrap">{invoice.notes}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Invoice Summary */}
          <div className="space-y-8">
            {/* Financial Summary */}
            {invoice.amount && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-mockup rounded-xl p-6"
              >
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                  <CurrencyDollarIcon className="w-6 h-6" />
                  <span>Summary</span>
                </h3>
                
                <div className="space-y-3">
                  <div className="border-t border-white/10 pt-3">
                    <div className="flex justify-between text-white font-semibold text-lg">
                      <span>Total:</span>
                      <span>{formatCurrency(invoice.amount)}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Payment Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">Payment Status</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className={`font-medium capitalize ${
                    invoice.paymentStatus === 'paid' ? 'text-green-400' : 
                    invoice.paymentStatus === 'partial' ? 'text-yellow-400' : 
                    'text-gray-400'
                  }`}>
                    {invoice.paymentStatus}
                  </span>
                </div>
                
                {invoice.paidDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Paid Date:</span>
                    <span className="text-white">{formatDate(invoice.paidDate)}</span>
                  </div>
                )}
                
                {invoice.paymentMethod && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Method:</span>
                    <span className="text-white">{invoice.paymentMethod}</span>
                  </div>
                )}
                
                {invoice.dueDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Due Date:</span>
                    <span className="text-white">{formatDate(invoice.dueDate)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-400">Uploaded:</span>
                  <span className="text-white">{formatDate(invoice.uploadedAt)}</span>
                </div>
              </div>
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button 
                  onClick={() => window.open(invoice.fileUrl, '_blank')}
                  className="w-full btn-primary flex items-center justify-center space-x-2"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>View Original File</span>
                </button>
                
                <Link to={`/uploaded-invoices/${invoice.id}/edit`} className="w-full">
                  <button className="w-full btn-secondary flex items-center justify-center space-x-2">
                    <PencilIcon className="w-5 h-5" />
                    <span>Edit Details</span>
                  </button>
                </Link>
                
                <button 
                  onClick={handleDeleteInvoice}
                  className="w-full btn-secondary text-red-400 hover:text-red-300 flex items-center justify-center space-x-2"
                >
                  <TrashIcon className="w-5 h-5" />
                  <span>Delete Invoice</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default UploadedInvoiceViewPage 